import { Button } from "@nextui-org/react";
import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CookiesConsent, useCookieConsentStore } from "./use-cookie-consent-store";

const variants = {
  open: { opacity: 1, y: 0, transition: { when: "beforeChildren", duration: 0.3, ease: "easeInOut" } },
  closed: { opacity: 0, y: 400, transition: { when: "afterChildren", duration: 0.3, ease: "easeInOut" } },
};

export default function CookieConsentModal() {
  const [isOpen, setIsOpen] = useState(true);
  const [showCookiesList, setShowCookiesList] = useState(false);
  const { updateCookiesConsent, cookiesConsent } = useCookieConsentStore();

  const [cookies, setCookies] = useState<Partial<CookiesConsent>>({
    desempenho: cookiesConsent.desempenho,
    publicidade: cookiesConsent.publicidade,
    funcionalidade: cookiesConsent.funcionalidade,
  });

  function handleClose() {
    setIsOpen(false);
  }

  function handleOpen() {
    setIsOpen(true);
  }

  return (
    <>
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            className="bg-[var(--main-color)] rounded-full shadow-2xl fixed bottom-4 left-4 z-[9999] cursor-pointer group overflow-hidden h-12 w-12 hover:w-[286px] transition-width duration-300"
            initial="closed"
            animate="open"
            exit="closed"
            variants={variants}
            onClick={handleOpen}
          >
            <span className="w-[276px] flex items-center justify-center gap-x-2">
              <span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
                  <path d="M8.5 8.5v.01" />
                  <path d="M16 15.5v.01" />
                  <path d="M12 12v.01" />
                  <path d="M11 17v.01" />
                  <path d="M7 14v.01" />
                </svg>
              </span>
              <motion.span className="font-kumbh-sans text-sm">Gerenciar preferências de cookies</motion.span>
            </span>
          </motion.button>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="bg-white max-w-full md:max-w-[460px] w-full py-[15px] px-[20px] rounded-xl grid gap-y-4 shadow-2xl fixed md:fixed bottom-0 md:bottom-4 left-0 md:left-4 z-[9999]"
            initial="closed"
            animate="open"
            exit="closed"
            layout="preserve-aspect"
            variants={variants}
          >
            <h3 className="font-bricolage-grotesque font-bold text-center">Este website utiliza cookies</h3>
            <p className="font-kumbh-sans leading-normal text-xs md:text-sm text-zinc-800">
              Usamos cookies para melhorar a experiência do usuário. Escolha quais cookies você nos permite utilizar. Você pode ler mais
              sobre em nossa{" "}
              <a
                href="https://ivienergia.com/termos-de-uso-e-politica-de-privacidade"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-blue-600"
              >
                Política de Privacidade
              </a>{" "}
              e{" "}
              <a
                href="https://ivienergia.com/politica-de-cookies"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-blue-600"
              >
                Política de Cookies
              </a>
              .
            </p>

            <div className="data-[show='false']:hidden" data-show={showCookiesList}>
              <ul className="space-y-2">
                <li>
                  <Cookie
                    title="Necessários"
                    description="são essenciais para o funcionamento dos serviços e recursos disponibilizados pelo site. Sem eles, o site não funciona ou apresenta mau desempenho"
                    toggle={<p className="text-sm font-kumbh-sans text-green-700">Sempre ativado</p>}
                  />
                </li>
                <li>
                  <Cookie
                    title="Desempenho"
                    description="são cookies que contribuem com experiência de uso do site ao coletar informações sobre as páginas acessadas, se apresentam erro etc."
                    toggle={
                      <ToggleSwitch
                        checked={!!cookies.desempenho}
                        onChange={({ target }) =>
                          setCookies({
                            ...cookies,
                            desempenho: target.checked,
                          })
                        }
                      />
                    }
                  />
                </li>
                <li>
                  <Cookie
                    title="Publicidade"
                    description="são cookies que divulgam anúncios em função dos seus interesses e limitam a quantidade de vezes que o anúncio aparece."
                    toggle={
                      <ToggleSwitch
                        checked={!!cookies.publicidade}
                        onChange={({ target }) =>
                          setCookies({
                            ...cookies,
                            publicidade: target.checked,
                          })
                        }
                      />
                    }
                  />
                </li>
                <li>
                  <Cookie
                    title="Funcionalidade"
                    description="são cookies que memorizam suas preferências e escolhas (como nome de usuário)."
                    toggle={
                      <ToggleSwitch
                        checked={!!cookies.funcionalidade}
                        onChange={({ target }) =>
                          setCookies({
                            ...cookies,
                            funcionalidade: target.checked,
                          })
                        }
                      />
                    }
                  />
                </li>
              </ul>
            </div>

            <div className="flex *:flex-1 gap-x-3 group" data-show={showCookiesList}>
              <Button
                className="bg-[var(--main-color)]"
                disableRipple
                onClick={() => {
                  setCookies({
                    desempenho: true,
                    publicidade: true,
                    funcionalidade: true,
                  });
                  updateCookiesConsent({
                    desempenho: true,
                    publicidade: true,
                    funcionalidade: true,
                  });
                  setShowCookiesList(false);
                  handleClose();
                }}
              >
                Aceitar todos
              </Button>
              <Button className="bg-zinc-100 hidden group-data-[show='false']:flex" disableRipple onClick={() => setShowCookiesList(true)}>
                Definição de Cookies
              </Button>

              <Button
                className="bg-zinc-100 group-data-[show='false']:hidden"
                disableRipple
                onClick={() => {
                  updateCookiesConsent(cookies);
                  setShowCookiesList(false);
                  handleClose();
                }}
              >
                Salvar Preferências
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

type CookieItemProps = React.ComponentProps<"details"> & {
  title: string;
  description: string;
  toggle: React.ReactNode;
};

function Cookie({ title, description, toggle, ...props }: CookieItemProps) {
  return (
    <details className="group bg-zinc-100 rounded-md" {...props}>
      <summary className="cursor-pointer flex px-3 py-2">
        <span className="flex items-center justify-between w-full">
          <span className="flex items-center justify-between w-full mr-2">
            <span className="text-sm font-kumbh-sans">{title}</span>
            {toggle}
          </span>
          <span className="group-open:rotate-180 transition-all duration-300">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" height="18">
              <path d="M224 416c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L224 338.8l169.4-169.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-192 192C240.4 412.9 232.2 416 224 416z"></path>
            </svg>
          </span>
        </span>
      </summary>

      <p className="font-kumbh-sans text-xs md:text-sm text-zinc-800 px-3 pb-3">{description}</p>
    </details>
  );
}

type ToggleSwitchProps = React.ComponentProps<"input">;

function ToggleSwitch(props: ToggleSwitchProps) {
  return (
    <label className="relative inline-flex items-center cursor-pointer ml-2">
      <input type="checkbox" className="sr-only peer" {...props} />
      <div className="w-7 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-blue-600"></div>
    </label>
  );
}
